import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { UnauthorizedPage } from "@/components/error-page";
import { PendingPage } from "@/components/pending-page";
import { profileQuery } from "@/services/queries";
import { can, hiddenFrom } from "@/utils/permissions";

/**
 * The permission gate, for a screen whose address must not open it.
 *
 * Hiding a menu entry is not a guard: the URL can be typed, bookmarked, or
 * followed from an old link. This refuses the render and says why. The API
 * refuses independently — this is so a person sees an answer instead of a
 * screen full of failed requests.
 *
 * It waits for the profile rather than assuming the worst, so a slow /me does
 * not flash Unauthorized at someone who is authorized.
 */
export function RequirePermission({
  anyOf,
  noneOf,
  children,
}: {
  /** Any one codename is enough, matching how the backend reads a requirement. */
  anyOf: string[];
  /**
   * Holding any one of these refuses, whatever `anyOf` said.
   *
   * A second question rather than more answers to the first. `anyOf` asks
   * whether the data may be read, which the API asks too; this asks whether
   * somebody has taken the screen out of this person's interface, which only
   * the browser asks. Someone given `view__club` so that clubs fill a dropdown
   * elsewhere, and `hide__club` to keep Business Unit out of their sidebar,
   * gets the same refusal from a typed address that the missing row implied.
   *
   * Empty or absent means nothing to ask, which is the usual case.
   */
  noneOf?: string[];
  children: ReactNode;
}) {
  const { data: profile, isPending, isError } = useQuery(profileQuery());
  if (isPending) return <PendingPage />;
  // A profile that could not be read is not a permission answer. The session
  // gate and the error boundary own that case; refusing here would blame the
  // account for the network.
  if (isError) return <>{children}</>;
  // `hiddenFrom` rather than `can` for the second half. `can` says yes to a
  // superuser for every codename that exists, `hide__*` included — so asking
  // it here refused every page to the one account meant to reach all of them.
  const permitted =
    can(profile, ...anyOf) && !(noneOf?.length && hiddenFrom(profile, ...noneOf));
  return permitted ? <>{children}</> : <UnauthorizedPage />;
}
